<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { deleteProjeto, listProjetos } from '@/api/projetos'
import { getApiErrorMessage } from '@/api/client'
import type { Projeto } from '@/types/projetos'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const router = useRouter()
const projetos = ref<Projeto[]>([])
const loading = ref(true)
const removingId = ref<string | null>(null)

async function load(): Promise<void> {
  loading.value = true
  try {
    projetos.value = await listProjetos()
  } catch (error) {
    projetos.value = []
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao carregar projetos',
      text: getApiErrorMessage(error, 'Não foi possível listar os projetos'),
    })
  } finally {
    loading.value = false
  }
}

async function remove(projeto: Projeto): Promise<void> {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Remover projeto?',
    text: 'As sessões existentes permanecem, mas o projeto deixa de aceitar gravações.',
    showCancelButton: true,
    confirmButtonText: 'Remover',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#dc3545',
  })

  if (!result.isConfirmed) return

  removingId.value = projeto.id
  try {
    await deleteProjeto(projeto.id)
    projetos.value = projetos.value.filter((item) => item.id !== projeto.id)
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Não foi possível remover',
      text: getApiErrorMessage(error, 'Erro ao remover projeto'),
    })
  } finally {
    removingId.value = null
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <AdminLayout>
    <template #title>
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <h1 class="h5 mb-1">Projetos</h1>
          <p class="text-muted small mb-0">Sites externos que enviam gravações de sessão</p>
        </div>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="loading" @click="load">
            <i class="bi bi-arrow-clockwise me-1" />
            Atualizar
          </button>
          <RouterLink to="/admin/projetos/novo" class="btn btn-primary btn-sm">
            <i class="bi bi-plus-lg me-1" />
            Novo projeto
          </RouterLink>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-muted">Carregando projetos…</div>

    <div v-else-if="projetos.length === 0" class="alert alert-light border">
      Nenhum projeto cadastrado. Crie um para obter a API key e o snippet de instalação.
    </div>

    <div v-else class="table-responsive card shadow-sm border-0">
      <table class="table table-hover align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th>Nome</th>
            <th>Slug</th>
            <th>Status</th>
            <th>Campos</th>
            <th class="text-end">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="projeto in projetos"
            :key="projeto.id"
            role="button"
            @click="router.push(`/admin/projetos/${projeto.id}`)"
          >
            <td class="fw-medium">{{ projeto.nome }}</td>
            <td><code>{{ projeto.slug }}</code></td>
            <td>
              <span class="badge" :class="projeto.ativo ? 'text-bg-success' : 'text-bg-secondary'">
                {{ projeto.ativo ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td class="text-muted small">{{ projeto.campos_metadados.length }} metadado(s)</td>
            <td class="text-end text-nowrap" @click.stop>
              <RouterLink :to="`/admin/projetos/${projeto.id}`" class="btn btn-sm btn-outline-primary me-1">
                Editar
              </RouterLink>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                :disabled="removingId === projeto.id"
                @click="remove(projeto)"
              >
                Remover
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </AdminLayout>
</template>
